import type { BranchColorRule } from '@clearflow/shared';
import type { GitgraphModel } from './buildGitgraphModel';

// Pré-compile les patterns une seule fois (pas par branche) — les règles invalides ont déjà été
// rejetées à l'écriture (BranchColorRule.pattern, packages/shared) mais on reste défensif ici
// (try/catch) au cas où une valeur stockée avant un durcissement futur du schéma ne compile plus.
function compileCustomRules(rules: BranchColorRule[]): Array<{ regex: RegExp; color: string }> {
  const compiled: Array<{ regex: RegExp; color: string }> = [];
  for (const rule of rules) {
    try {
      compiled.push({ regex: new RegExp(rule.pattern), color: rule.color });
    } catch {
      // ignore une règle qui ne compile plus
    }
  }
  return compiled;
}

export const DOT_R = 9;
export const CHAR_W = 7;
export const MAX_CHARS = 28;
export const LINE_H = 14;
export const BUBBLE_PAD_X = 10;
export const BUBBLE_PAD_Y = 8;
export const DASH_H = 22;

const COMMIT_GAP = 6;
const LEFT_PAD = 60;
// Minimum flat run a non-main branch must keep between its own diagonal and the
// dot at either end — a fork-in diagonal landing exactly on the first dot (or a
// merge-out diagonal starting exactly at the last dot) reads as a kink, not an
// intentional "branch runs flat, then bends" shape. Two separate constants (not
// one shared value) because they're tuned independently: MIN_ELBOW_GAP_IN moves
// dots (clean-diagonal pass below) so it's felt directly at every fork; MIN_ELBOW_GAP_OUT
// only gates which edge SHAPE gets picked at render time (no dot ever moves for it),
// so a change there is far less visible and doesn't need to track the other 1:1.
const MIN_ELBOW_GAP_IN  = DOT_R * 4;
const MIN_ELBOW_GAP_OUT = DOT_R * 4;
// Floor for the opposite-bubble-side compaction pass (below) — plain dot clearance
// (DOT_R*3) reads as too tight once the two bubbles are pulled apart from each
// other into separate above/below zones; a bit more breathing room than the bare
// same-lane minimum used elsewhere for actual dot-to-dot collision avoidance.
const MIN_OPPOSITE_SIDE_GAP = DOT_R * 12;
// Exportée pour BranchColorRulesModal.tsx, qui en propose une règle initiale pour "main" — évite
// que la couleur suggérée dérive silencieusement de celle réellement appliquée par défaut ici.
export const MAIN_BLUE = '#1f78d1';
const FEATURE_GREEN  = '#1aaa55';
const RELEASE_PURPLE = '#9b59b6';
const CHORE_YELLOW   = '#f0a500';
const EXTRA_COLORS   = ['#e24329', '#fc6d26', '#fca326', '#6b4fbb', '#e1007a'];

export const LABEL_H        = 20;
export const LABEL_CHAR_W   = 7;
export const LABEL_PAD_X    = 8;
const LABEL_ELBOW_ABOVE = 44;

export const TAG_H       = 18;
export const TAG_CHAR_W  = 6.5;
export const TAG_PAD_X   = 6;
const TAG_GAP = 4; // vertical gap between tag rect and dot, and between stacked tags

export interface LayoutNode {
  sha: string;
  subject: string;
  body: string;
  authorName: string;
  authoredAt: number; // Unix timestamp (seconds)
  x: number;
  y: number;
  color: string;
  branchName: string;
  bubbleAbove: boolean;
  bubbleDashH: number; // dash height; may exceed DASH_H to clear tag labels
}

export interface LayoutEdge {
  id: string;
  path: string;
  color: string;
  branchName: string;
  isMerge: boolean;
}

export interface LayoutBranchLabel {
  name: string;
  x: number;
  y: number;
  color: string;
  /** Elbow point on the branch edge to connect with a dashed line (absent for main) */
  elbowX?: number;
  elbowY?: number;
}

/** Ghost node rendered when a commit's first parent is absent from the dataset (history truncated). */
export interface TruncatedRoot {
  sha: string;
  childX: number;
  childY: number;
  x: number;
  y: number;
  color: string;
}

export interface Layout {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  branchLabels: LayoutBranchLabel[];
  truncatedRoots: TruncatedRoot[];
  openTips: LayoutNode[];
  trunkBranch: string;
  laneSpacing: number;
  svgWidth: number;
  svgHeight: number;
}

export function wrapText(text: string): string[] {
  if (text.length <= MAX_CHARS) return [text];
  const lines: string[] = [];
  let remaining = text;
  while (remaining.length > 0 && lines.length < 3) {
    if (remaining.length <= MAX_CHARS) { lines.push(remaining); break; }
    const sub = remaining.slice(0, MAX_CHARS + 1);
    const cut = sub.lastIndexOf(' ');
    if (cut <= 0) {
      lines.push(remaining.slice(0, MAX_CHARS));
      remaining = remaining.slice(MAX_CHARS);
    } else {
      lines.push(sub.slice(0, cut));
      remaining = remaining.slice(cut + 1);
    }
  }
  if (remaining.length > 0 && lines.length === 3) {
    const last = lines[2];
    lines[2] = last.length <= MAX_CHARS - 1 ? `${last}…` : `${last.slice(0, MAX_CHARS - 1)}…`;
  }
  return lines;
}

export function computeBubble(subject: string): { lines: string[]; boxW: number; boxH: number } {
  const firstLine = subject.split('\n')[0].trim();
  const lines = wrapText(firstLine);
  const maxLen = Math.max(...lines.map((l) => l.length), 0);
  const boxW = Math.max(maxLen * CHAR_W + BUBBLE_PAD_X * 2, 60);
  const boxH = lines.length * LINE_H + BUBBLE_PAD_Y;
  return { lines, boxW, boxH };
}

function branchColor(
  name: string,
  idx: number,
  trunkBranch: string,
  customRules: Array<{ regex: RegExp; color: string }>,
): string {
  // Règles perso (Repository.branchColorRules, éditées depuis la vue du dépôt) prioritaires sur
  // toute la logique par défaut ci-dessous — première qui matche gagne, voir packages/shared/src/
  // branchColors.ts. Une branche qui ne matche aucune règle garde le comportement historique.
  for (const rule of customRules) {
    if (rule.regex.test(name)) return rule.color;
  }
  if (name.startsWith('__orphan_'))               return '#555566';
  if (name === 'master' || /^main(-|\/|$)/.test(name))  return MAIN_BLUE;
  // Release branches get purple even when they happen to be the trunk (e.g. GitLab
  // default branch is release/* and no main/master exists elsewhere in the repo)
  if (/^release(\/|$)/.test(name))               return RELEASE_PURPLE;
  // Same semantic-trunk preference as trunkBranch above: only color a non-main/master
  // name blue when it's genuinely standing in for main (no real main/master in the repo),
  // not just because it happens to be GitLab's configured default branch.
  if (name === trunkBranch)                       return MAIN_BLUE;
  if (/(^|\/)release$/.test(name))               return CHORE_YELLOW;
  if (/^(feature|feat|fix|chore)\//.test(name))  return FEATURE_GREEN;
  return EXTRA_COLORS[(idx - 2 + EXTRA_COLORS.length * 100) % EXTRA_COLORS.length];
}

function topoSort(commits: GitgraphModel['commits']): GitgraphModel['commits'] {
  const map = new Map(commits.map((c) => [c.hash, c]));
  const visited = new Set<string>();
  const result: GitgraphModel['commits'] = [];
  function visit(hash: string) {
    if (visited.has(hash)) return;
    visited.add(hash);
    const c = map.get(hash);
    if (!c) return;
    for (const p of c.parents) visit(p);
    result.push(c);
  }
  for (const c of commits) visit(c.hash);
  return result; // parents before children
}

// ── Sugiyama layout ────────────────────────────────────────────────────────────
//
// computeLayout n'est plus qu'un orchestrateur : chaque phase est une fonction de module aux
// paramètres explicites (GU-363, pour la règle Sonar S3776). L'ORDRE des appels et la mutation en
// place des `LayoutNode` sont ceux de l'ancienne version monolithique — ne pas les réordonner :
// le solveur de positions est sensible à l'ordre (voir « Résolution des positions » dans CLAUDE.md).

type Commit = GitgraphModel['commits'][number];
type CherryPick = { sourceSha: string; targetSha: string };
type CustomRules = Array<{ regex: RegExp; color: string }>;

interface BranchIntervals {
  branchDivRank: Map<string, number>; // branch → rank of first commit
  branchDivCommit: Map<string, string>; // branch → sha of first commit
  ivEnd: Map<string, number>;
  openBranches: Set<string>;
}

interface BranchHierarchy {
  branchParentBranch: Map<string, string>;
  branchDepth: Map<string, number>;
  effectiveRank: (bn: string) => number;
}

// ── Phase 1: Rank — longest path from any root ─────────────────────────
// Commits at the same rank are "parallel" work; same X column in the layout.
// Also the backbone of the position solver below: rank strictly increases along
// every parent→child edge (rank[child] ≥ rank[parent]+1), so processing nodes in
// ascending-rank order is always a valid topological order for the position
// constraints built from those edges — no separate topological walk needed there.
function computeRanks(topo: Commit[]): Map<string, number> {
  const rank = new Map<string, number>();
  for (const c of topo) {
    rank.set(
      c.hash,
      c.parents.length === 0
        ? 0
        : Math.max(...c.parents.map((p) => (rank.get(p) ?? 0) + 1)),
    );
  }
  return rank;
}

// ── Phase 2: Branch of each commit ─────────────────────────────────────
// Backward pass: unclaimed commits (refs=[]) shouldn't all share a single lane.
// Strategy: inherit the first-parent child's branch when possible (keeps the commit
// on its natural lane). Otherwise, start a new orphan chain keyed by the commit's
// own SHA — each disconnected orphan chain gets a unique virtual branch and thus
// its own lane, placed after all real branches by the Phase-3 sort.
function assignBranchOfCommit(topo: Commit[], trunkBranch: string): Map<string, string> {
  const branchOfCommit = new Map<string, string>();
  for (const c of topo) branchOfCommit.set(c.hash, c.refs[0] ?? trunkBranch);

  const firstParentChildOf = new Map<string, string>();
  for (const c of topo) {
    if (c.parents.length > 0) firstParentChildOf.set(c.parents[0], c.hash);
  }
  for (const c of [...topo].reverse()) {
    if (c.refs.length > 0) continue;
    const childHash = firstParentChildOf.get(c.hash);
    const childBranch = childHash ? branchOfCommit.get(childHash) : undefined;
    if (childBranch && childBranch !== trunkBranch) {
      branchOfCommit.set(c.hash, childBranch);
      continue;
    }
    // No real-branch child → new orphan virtual branch rooted at this commit. Hash complet (pas
    // tronqué) : cette clé n'est jamais affichée, un raccourci n'apporterait aucune lisibilité
    // en échange du (fût-il infime) risque de collision entre deux racines orphelines.
    branchOfCommit.set(c.hash, `__orphan_${c.hash}__`);
  }
  return branchOfCommit;
}

// interval.end = max(own last commit rank, rank of any commit that merges FROM this branch)
function computeBaseIntervals(
  topo: Commit[],
  rank: Map<string, number>,
  branchOfCommit: Map<string, string>,
): Pick<BranchIntervals, 'branchDivRank' | 'branchDivCommit' | 'ivEnd'> {
  const branchDivRank = new Map<string, number>();
  const branchDivCommit = new Map<string, string>();
  for (const c of topo) {
    const bn = branchOfCommit.get(c.hash)!;
    if (!branchDivRank.has(bn)) {
      branchDivRank.set(bn, rank.get(c.hash)!);
      branchDivCommit.set(bn, c.hash);
    }
  }

  const ivEnd = new Map<string, number>(
    [...branchDivRank.entries()].map(([bn, r]) => [bn, r]),
  );
  for (const c of topo) {
    const cBn = branchOfCommit.get(c.hash)!;
    const r  = rank.get(c.hash)!;
    ivEnd.set(cBn, Math.max(ivEnd.get(cBn)!, r));
    for (const p of c.parents) {
      const pBn = branchOfCommit.get(p);
      if (pBn && pBn !== cBn) ivEnd.set(pBn, Math.max(ivEnd.get(pBn)!, r));
    }
  }
  return { branchDivRank, branchDivCommit, ivEnd };
}

// Extra pass: extend ivEnd for branches named in merge-commit subjects whose tip
// commits fall outside the backend's per-branch fetch window and are therefore
// absent from branchOfCommit. Without this, their interval ends prematurely and
// they collapse onto the same lane as a concurrent branch.
const SUBJECT_MERGE_RE = /^Merge (?:remote-tracking )?branch '([^']+)'(?:\s+into\b)?/;
const SUBJECT_PR_RE    = /^Merge pull request #\d+ from [^/]+\/(.+)/;
function extendIntervalsFromMergeSubjects(
  topo: Commit[],
  rank: Map<string, number>,
  branchDivRank: Map<string, number>,
  ivEnd: Map<string, number>,
): void {
  for (const c of topo) {
    if (c.parents.length < 2) continue;
    const m = SUBJECT_MERGE_RE.exec(c.subject) ?? SUBJECT_PR_RE.exec(c.subject);
    if (!m) continue;
    let src = m[1].trim();
    if (src.startsWith('origin/')) src = src.slice(7);
    if (!branchDivRank.has(src)) continue;
    const r = rank.get(c.hash)!;
    ivEnd.set(src, Math.max(ivEnd.get(src) ?? 0, r));
  }
}

// Cherry-pick lane release: when the last commit of a branch was cherry-picked
// rather than merged, cap ivEnd at that commit's rank. The merge-subject pass
// above can incorrectly extend ivEnd for such branches (e.g. a closed PR still
// appears in a merge subject), keeping the lane occupied too long.
// Renvoie aussi branchLastRank, réutilisé par la marge des tips ouverts ci-dessous.
function capIntervalsForCherryPicks(
  topo: Commit[],
  rank: Map<string, number>,
  branchOfCommit: Map<string, string>,
  ivEnd: Map<string, number>,
  cherryPicks: CherryPick[],
): Map<string, number> {
  const branchLastRank = new Map<string, number>();
  for (const c of topo) {
    const bn = branchOfCommit.get(c.hash)!;
    const r  = rank.get(c.hash)!;
    if (r > (branchLastRank.get(bn) ?? -1)) branchLastRank.set(bn, r);
  }
  for (const cp of cherryPicks) {
    const srcBn   = branchOfCommit.get(cp.sourceSha);
    const srcRank = rank.get(cp.sourceSha);
    if (srcBn === undefined || srcRank === undefined) continue;
    if (srcRank !== branchLastRank.get(srcBn)) continue; // not the last commit
    if ((ivEnd.get(srcBn) ?? 0) > srcRank) {
      ivEnd.set(srcBn, srcRank);
    }
  }
  return branchLastRank;
}

// Open-tip margin: a branch whose last commit is not merged gets a tip arrow (TIP_LEN px plus
// the marker overshoot, see GitGraph) that the rank-based interval knows nothing about. Without
// a margin, a branch starting one rank later on the same lane has its fork-in landing under that
// arrow (found 2026-10-07, repo "udm-monitoring": fix/UDMP-135 / feat/UDMP-143). One extra rank
// is a heuristic, not an X-space guarantee — see the "Tips ouverts" notes in CLAUDE.md.
function applyOpenTipMargin(
  topo: Commit[],
  rank: Map<string, number>,
  branchOfCommit: Map<string, string>,
  branchLastRank: Map<string, number>,
  ivEnd: Map<string, number>,
  trunkBranch: string,
): Set<string> {
  const nonFirstParentShas = new Set<string>();
  for (const c of topo) {
    for (let i = 1; i < c.parents.length; i++) nonFirstParentShas.add(c.parents[i]);
  }
  const openBranches = new Set<string>();
  for (const c of topo) {
    const bn = branchOfCommit.get(c.hash)!;
    if (rank.get(c.hash) === branchLastRank.get(bn) && !nonFirstParentShas.has(c.hash)) {
      openBranches.add(bn);
    }
  }
  for (const bn of openBranches) {
    if (bn !== trunkBranch) ivEnd.set(bn, ivEnd.get(bn)! + 1);
  }
  return openBranches;
}

// ── Phase 2: Branch intervals ──────────────────────────────────────────
// Each branch is "active" from its first commit to the last rank it touches,
// including any merge commit that pulls from it (the merge edge exits on the
// lane of this branch and arrives at the target lane).
function computeBranchIntervals(
  topo: Commit[],
  rank: Map<string, number>,
  branchOfCommit: Map<string, string>,
  trunkBranch: string,
  cherryPicks: CherryPick[],
): BranchIntervals {
  const { branchDivRank, branchDivCommit, ivEnd } = computeBaseIntervals(topo, rank, branchOfCommit);
  extendIntervalsFromMergeSubjects(topo, rank, branchDivRank, ivEnd);
  const branchLastRank = capIntervalsForCherryPicks(topo, rank, branchOfCommit, ivEnd, cherryPicks);
  const openBranches = applyOpenTipMargin(topo, rank, branchOfCommit, branchLastRank, ivEnd, trunkBranch);
  return { branchDivRank, branchDivCommit, ivEnd, openBranches };
}

// ── Phase 2.5: Branch nesting depth ───────────────────────────────────
// A branch that diverged from a non-main branch should always sit in a
// higher lane than the branch it branched off. We compute depth by BFS
// from defaultBranch using first-parent lineage at divergence points.
function computeBranchParents(
  topo: Commit[],
  branchOfCommit: Map<string, string>,
  branchDivCommit: Map<string, string>,
  trunkBranch: string,
): Map<string, string> {
  const topoCommitMap = new Map(topo.map((c) => [c.hash, c]));
  const branchParentBranch = new Map<string, string>();
  for (const [bn, sha] of branchDivCommit.entries()) {
    if (bn === trunkBranch) continue;
    const parentHash = topoCommitMap.get(sha)?.parents[0];
    const parentBranch = parentHash ? (branchOfCommit.get(parentHash) ?? trunkBranch) : trunkBranch;
    branchParentBranch.set(bn, parentBranch === bn ? trunkBranch : parentBranch);
  }
  return branchParentBranch;
}

function computeBranchDepths(
  branchParentBranch: Map<string, string>,
  branchDivRank: Map<string, number>,
  trunkBranch: string,
): Map<string, number> {
  const branchDepth = new Map<string, number>([[trunkBranch, 0]]);
  let frontier = [trunkBranch];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const parent of frontier) {
      const d = branchDepth.get(parent)!;
      for (const [bn, p] of branchParentBranch.entries()) {
        if (p === parent && !branchDepth.has(bn)) {
          branchDepth.set(bn, d + 1);
          next.push(bn);
        }
      }
    }
    frontier = next;
  }
  for (const bn of branchDivRank.keys()) {
    if (!branchDepth.has(bn)) branchDepth.set(bn, 1);
  }
  return branchDepth;
}

// Une branche protégée hors trunk (ex. main-next) est un second trunk de facto : elle diverge
// typiquement très tôt et ne se referme jamais. Triée par rang de divergence pur (ci-dessous),
// elle réserve donc systématiquement la toute première lane libre près du tronc, la bloque pour
// la quasi-totalité de l'historique, et force chaque branche concurrente vers l'extérieur — d'où
// beaucoup de croisements visuels contre elle. effectiveRank la reporte après toutes les branches
// non protégées dans l'ordre de traitement (Phase 3), pour qu'elle récupère une lane extérieure
// neuve plutôt que d'en bloquer une intérieure. Plafonnée juste avant son propre descendant le
// plus précoce (s'il en a un) : la contrainte de profondeur ci-dessous suppose que le parent a
// déjà sa lane assignée quand l'enfant est traité — la faire passer après son propre enfant
// inverserait cette hypothèse plutôt que de simplement changer qui obtient quelle lane.
function makeEffectiveRank(
  branchDivRank: Map<string, number>,
  branchParentBranch: Map<string, string>,
  protectedBranches: Set<string>,
): (bn: string) => number {
  const protectedDescendantMinRank = new Map<string, number>();
  for (const bn of branchDivRank.keys()) {
    let cur = branchParentBranch.get(bn);
    while (cur !== undefined) {
      if (protectedBranches.has(cur)) {
        const r = branchDivRank.get(bn)!;
        const prev = protectedDescendantMinRank.get(cur);
        if (prev === undefined || r < prev) protectedDescendantMinRank.set(cur, r);
      }
      cur = branchParentBranch.get(cur);
    }
  }
  const maxDivRank = Math.max(...branchDivRank.values());
  return (bn: string): number => {
    if (!protectedBranches.has(bn)) return branchDivRank.get(bn)!;
    const ceiling = protectedDescendantMinRank.get(bn);
    return ceiling === undefined ? maxDivRank + 1 : ceiling - 1;
  };
}

function computeBranchHierarchy(
  topo: Commit[],
  branchOfCommit: Map<string, string>,
  intervals: BranchIntervals,
  trunkBranch: string,
  protectedBranches: Set<string>,
): BranchHierarchy {
  const branchParentBranch = computeBranchParents(topo, branchOfCommit, intervals.branchDivCommit, trunkBranch);
  const branchDepth = computeBranchDepths(branchParentBranch, intervals.branchDivRank, trunkBranch);
  const effectiveRank = makeEffectiveRank(intervals.branchDivRank, branchParentBranch, protectedBranches);
  return { branchParentBranch, branchDepth, effectiveRank };
}

// ── Phase 3: Lane assignment — greedy interval-graph colouring ─────────
// Two branches share a lane iff their active intervals are strictly disjoint.
// This is the optimal (minimum-lane) solution for interval graphs.
const isOrphan = (bn: string) => bn.startsWith('__orphan_');

function sortBranchesByStart(
  trunkBranch: string,
  { branchDivRank, ivEnd }: BranchIntervals,
  { branchDepth, effectiveRank }: BranchHierarchy,
): string[] {
  return [...branchDivRank.keys()]
    .filter((bn) => bn !== trunkBranch)
    .sort((a, b) => {
      // Primary: start time — greedy interval coloring is only optimal when
      // branches are processed in start-time order. Sorting depth-first breaks
      // this: a depth-1 branch that starts later than a depth-2 branch would
      // steal a low lane and block the depth-2 branch from reusing it. effectiveRank
      // is the real divRank for every branch except a protected one (see above), which
      // makes this comparison a no-op change for the (overwhelming majority of) repos
      // without a protected non-trunk branch.
      const rankDiff = effectiveRank(a) - effectiveRank(b);
      if (rankDiff !== 0) return rankDiff;
      // Secondary: shallower branches get lower lanes when starting simultaneously.
      const depthDiff = (branchDepth.get(a) ?? 1) - (branchDepth.get(b) ?? 1);
      if (depthDiff !== 0) return depthDiff;
      // Orphans after real branches only as a tie-breaker within the same divRank
      const aOrph = isOrphan(a), bOrph = isOrphan(b);
      if (aOrph !== bOrph) return aOrph ? 1 : -1;
      // Tie-break: shorter-lived branches get lower lanes (closer to main)
      const lenA = (ivEnd.get(a) ?? 0) - branchDivRank.get(a)!;
      const lenB = (ivEnd.get(b) ?? 0) - branchDivRank.get(b)!;
      return lenA - lenB;
    });
}

// Depth constraint: this branch must sit in a lane strictly above every
// already-assigned branch of lower depth whose interval overlaps ours.
// Because we process in divRank order, the parent branch (which has
// divRank ≤ ours) is always assigned before us, so the constraint is
// always computable for the parent. Non-overlapping lower-depth branches
// impose no constraint and can share a lane freely.
function minLaneForDepth(
  bn: string,
  branchLane: Map<string, number>,
  { branchDivRank, ivEnd }: BranchIntervals,
  branchDepth: Map<string, number>,
): number {
  const start = branchDivRank.get(bn)!;
  const end   = ivEnd.get(bn)!;
  const depth = branchDepth.get(bn) ?? 1;
  let minLane = 1;
  for (const [otherBn, otherLane] of branchLane.entries()) {
    if ((branchDepth.get(otherBn) ?? 0) >= depth) continue;
    const otherStart = branchDivRank.get(otherBn)!;
    const otherEnd   = ivEnd.get(otherBn)!;
    if (start <= otherEnd && end >= otherStart) {
      minLane = Math.max(minLane, otherLane + 1);
    }
  }
  return minLane;
}

function greedyLaneColoring(
  byDivRank: string[],
  trunkBranch: string,
  intervals: BranchIntervals,
  branchDepth: Map<string, number>,
): Map<string, number> {
  const branchLane = new Map<string, number>();
  branchLane.set(trunkBranch, 0);
  // laneEnd[k] = ivEnd of the last branch assigned to lane k
  const laneEnd: number[] = [Infinity]; // lane 0 = main, permanently occupied

  for (const bn of byDivRank) {
    const start = intervals.branchDivRank.get(bn)!;
    const end   = intervals.ivEnd.get(bn)!;
    const minLane = minLaneForDepth(bn, branchLane, intervals, branchDepth);

    // Smallest lane ≥ minLane whose last branch ended strictly before this one starts
    let lane = minLane;
    for (; lane < laneEnd.length; lane++) {
      if (laneEnd[lane] < start) break;
    }
    if (lane === laneEnd.length) laneEnd.push(-Infinity); // open a new lane
    branchLane.set(bn, lane);
    laneEnd[lane] = Math.max(laneEnd[lane], end);
  }
  return branchLane;
}

const ivOverlap = ({ branchDivRank, ivEnd }: BranchIntervals, a: string, b: string) =>
  !(ivEnd.get(a)! < branchDivRank.get(b)! || ivEnd.get(b)! < branchDivRank.get(a)!);

// Can `m` sit on `lane` if `partner` (the other half of the swap) is moved away at the same time?
function canTakeLane(
  m: string,
  lane: number,
  partner: string,
  branchLane: Map<string, number>,
  intervals: BranchIntervals,
  branchDepth: Map<string, number>,
): boolean {
  for (const [o, oLane] of branchLane) {
    if (o === m || o === partner || !ivOverlap(intervals, m, o)) continue;
    if (oLane === lane) return false;
    const od = branchDepth.get(o) ?? 0;
    const md = branchDepth.get(m) ?? 1;
    if (od < md && oLane >= lane) return false;
    if (od > md && oLane <= lane) return false;
  }
  return true;
}

// ── Open-sibling swap: a later fork takes the inner lane ───────────────────
// Greedy colouring gives the EARLIER branch the lower lane. For two open branches forked from
// the same parent on adjacent lanes, that makes the later one's fork-in diagonal cross the
// earlier one's lane, right on its tip arrow (found 2026-10-08, repo "udm-monitoring":
// fix/UDMP-135 / feat/UDMP-143). Swapping them removes the crossing: the earlier fork-in then
// crosses the inner lane before the later branch starts. Local post-pass — the greedy
// start-time ordering above, and its lane-count optimality, are left untouched. Skipped
// unless the swap provably keeps the lane/depth invariants (see canTakeLane), and for branches
// with children (their depth constraint is anchored on these lanes).
function swapOpenSiblings(
  branchLane: Map<string, number>,
  byDivRank: string[],
  intervals: BranchIntervals,
  { branchParentBranch, branchDepth }: BranchHierarchy,
): void {
  const { branchDivRank, ivEnd, openBranches } = intervals;
  const hasChildBranch = new Set(branchParentBranch.values());
  for (const b of byDivRank) {
    if (isOrphan(b) || b === 'chore/release' || hasChildBranch.has(b)) continue;
    const laneB = branchLane.get(b)!;
    const bStart = branchDivRank.get(b)!;
    // Latest-starting open sibling on the adjacent inner lane whose tip is still there at b's fork.
    const a = byDivRank
      .filter((x) =>
        x !== b && openBranches.has(x) && !isOrphan(x) && x !== 'chore/release' && !hasChildBranch.has(x) &&
        branchLane.get(x) === laneB - 1 &&
        branchParentBranch.get(x) === branchParentBranch.get(b) &&
        (branchDepth.get(x) ?? 1) === (branchDepth.get(b) ?? 1) &&
        branchDivRank.get(x)! < bStart && bStart <= ivEnd.get(x)!)
      .sort((x, y) => branchDivRank.get(y)! - branchDivRank.get(x)!)[0];
    if (a === undefined || laneB - 1 < 1) continue;
    if (!canTakeLane(a, laneB, b, branchLane, intervals, branchDepth)
      || !canTakeLane(b, laneB - 1, a, branchLane, intervals, branchDepth)) continue;
    branchLane.set(a, laneB);
    branchLane.set(b, laneB - 1);
  }
}

// ── "chore/release" rides the rail directly above its source branch ────────
// Explicit, name-specific rule (not a general invariant): every other branch
// sits strictly BELOW (farther from main) the branch it diverged from, per
// the depth constraint above. chore/release is the opposite — it must render
// one rail ABOVE main specifically when it diverged from main. Lane 0 is
// otherwise permanently reserved for trunk (laneEnd[0] = Infinity above), so
// there is no free lane above it: every lane is shifted down by one to open
// lane 0 for chore/release, which then takes it over. Scoped to "diverged
// from main" only — if chore/release ever forks from something other than
// main, this rule doesn't (and isn't asked to) reposition it; it keeps
// whatever lane the normal depth-constrained placement above gave it.
function shiftLanesForChoreRelease(
  branchLane: Map<string, number>,
  branchDivRank: Map<string, number>,
  branchParentBranch: Map<string, string>,
  trunkBranch: string,
): void {
  if (!branchDivRank.has('chore/release') || branchParentBranch.get('chore/release') !== trunkBranch) return;
  for (const [bn, lane] of branchLane) {
    branchLane.set(bn, bn === 'chore/release' ? 0 : lane + 1);
  }
}

// Debug: log lane assignments — remove after diagnosis
function logLaneDebug(
  branchLane: Map<string, number>,
  byDivRank: string[],
  { branchDivRank, ivEnd }: BranchIntervals,
  branchDepth: Map<string, number>,
): void {
  if (typeof window === 'undefined' || !localStorage.getItem('clearflow_debug_lanes')) return;
  console.table(
    [...branchDivRank.keys()].map((bn) => ({
      branch: bn,
      depth:    branchDepth.get(bn) ?? 0,
      divRank:  branchDivRank.get(bn),
      ivEnd:    ivEnd.get(bn),
      lane:     branchLane.get(bn) ?? 0,
      blockedBy: branchLane.get(bn) !== undefined && branchLane.get(bn)! > 1
        ? byDivRank
            .slice(0, byDivRank.indexOf(bn))
            .filter((other) => {
              const otherEnd = ivEnd.get(other) ?? 0;
              const start    = branchDivRank.get(bn)!;
              return branchLane.get(other) === 1 && otherEnd >= start;
            })
            .join(', ') || '—'
        : '—',
    })),
  );
}

function assignLanes(
  trunkBranch: string,
  intervals: BranchIntervals,
  hierarchy: BranchHierarchy,
): { branchLane: Map<string, number>; byDivRank: string[] } {
  const byDivRank = sortBranchesByStart(trunkBranch, intervals, hierarchy);
  const branchLane = greedyLaneColoring(byDivRank, trunkBranch, intervals, hierarchy.branchDepth);
  swapOpenSiblings(branchLane, byDivRank, intervals, hierarchy);
  shiftLanesForChoreRelease(branchLane, intervals.branchDivRank, hierarchy.branchParentBranch, trunkBranch);
  logLaneDebug(branchLane, byDivRank, intervals, hierarchy.branchDepth);
  return { branchLane, byDivRank };
}

// ── Phase 4: Column X — one column per rank ────────────────────────────
interface Columns {
  colX: Map<number, number>; // rank → center X
  bubbleW: Map<string, number>; // sha → bubble width
  laneSpacing: number;
}

// Lane spacing (vertical gap between lanes).  Computed before the column sizing —
// because a lane-spanning edge needs ≥ dy/4 of horizontal room for its slope to reach
// ±4, and dy is a multiple of laneSpacing.
function computeLaneSpacing(topo: Commit[]): number {
  const maxBubbleH = Math.max(
    ...topo.map((c) => computeBubble(c.subject).boxH),
    LINE_H + BUBBLE_PAD_Y,
  );
  const belowSpace = DOT_R + DASH_H + maxBubbleH;
  const aboveSpace = Math.max(LABEL_ELBOW_ABOVE + LABEL_H / 2 + 16, belowSpace);
  return Math.max(70, aboveSpace + belowSpace) + 20;
}

// Inter-rank spacing: driven by commits connected by an edge.  Two constraints:
//  (a) bubbles of adjacent-rank connected commits must not overlap;
//  (b) a lane-spanning edge needs ≥ dy/4 of horizontal room so its diagonal can
//      be ±4 instead of steeper (a too-narrow column forces a steep diagonal).
function computeInterRankSpacing(
  topo: Commit[],
  rank: Map<string, number>,
  bubbleW: Map<string, number>,
  laneOf: (hash: string) => number,
  laneSpacing: number,
): Map<number, number> {
  const interRankSpacing = new Map<number, number>();
  for (const c of topo) {
    const rc    = rank.get(c.hash)!;
    const cBW   = bubbleW.get(c.hash)!;
    const cLane = laneOf(c.hash);
    for (const pHash of c.parents) {
      const rp = rank.get(pHash);
      if (rp !== rc - 1) continue;
      const pBW     = bubbleW.get(pHash) ?? 60;
      const laneSep = Math.abs(cLane - laneOf(pHash));
      let need = pBW / 2 + COMMIT_GAP + cBW / 2;
      if (laneSep > 0) need = Math.max(need, (laneSep * laneSpacing) / 4);
      interRankSpacing.set(rp, Math.max(interRankSpacing.get(rp) ?? 0, need));
    }
  }
  return interRankSpacing;
}

function computeColumns(
  topo: Commit[],
  rank: Map<string, number>,
  branchOfCommit: Map<string, string>,
  branchLane: Map<string, number>,
  trunkBranch: string,
): Columns {
  const maxRank = Math.max(...rank.values());
  const colMaxW = new Map<number, number>(); // rank → widest bubble (for first-rank padding + svgWidth)
  const bubbleW = new Map<string, number>(); // sha  → bubble width
  for (const c of topo) {
    const r = rank.get(c.hash)!;
    const { boxW } = computeBubble(c.subject);
    bubbleW.set(c.hash, boxW);
    colMaxW.set(r, Math.max(colMaxW.get(r) ?? 0, boxW));
  }

  const laneSpacing = computeLaneSpacing(topo);
  const laneOf = (hash: string) => branchLane.get(branchOfCommit.get(hash) ?? trunkBranch) ?? 0;
  const interRankSpacing = computeInterRankSpacing(topo, rank, bubbleW, laneOf, laneSpacing);

  const colX = new Map<number, number>();
  colX.set(0, LEFT_PAD + (colMaxW.get(0) ?? 60) / 2);
  for (let r = 0; r < maxRank; r++) {
    colX.set(r + 1, colX.get(r)! + Math.max(interRankSpacing.get(r) ?? 0, DOT_R * 3));
  }
  return { colX, bubbleW, laneSpacing };
}

// ── Build nodes ─────────────────────────────────────────────────────────
// Bubble side per commit: tagged commits and branch-head commits always get the bubble below;
// others alternate by rank parity (odd rank → above). Computed once, up front —
// independent of x — except for the handful of collision flips fixBubbleLabelTagCollisions
// makes later (always true→false, see below), which is why the position solver
// re-runs after those flips instead of trusting this initial guess forever.
function buildLayoutNodes(
  topo: Commit[],
  model: GitgraphModel,
  rank: Map<string, number>,
  branchOfCommit: Map<string, string>,
  branchLane: Map<string, number>,
  colors: Map<string, string>,
  colX: Map<number, number>,
  laneSpacing: number,
): LayoutNode[] {
  const taggedShas = new Set(Object.values(model.tags));
  const branchLabelShas = new Set<string>();
  const seenLabelBranches = new Set<string>();
  for (const c of topo) {
    const bn = branchOfCommit.get(c.hash)!;
    if (model.branchHeads[bn] && !seenLabelBranches.has(bn)) {
      seenLabelBranches.add(bn);
      branchLabelShas.add(c.hash);
    }
  }

  return topo.map((c) => {
    const bn = branchOfCommit.get(c.hash)!;
    const r  = rank.get(c.hash)!;
    const bubbleAbove = r % 2 === 1
      && !taggedShas.has(c.hash)
      && !branchLabelShas.has(c.hash);
    return {
      sha: c.hash,
      subject: c.subject,
      body: c.body,
      authorName: c.author.name,
      authoredAt: c.author.timestamp,
      x: colX.get(r)!,
      y: (branchLane.get(bn)! + 1) * laneSpacing,
      color: colors.get(bn) ?? MAIN_BLUE,
      branchName: bn,
      bubbleAbove,
      bubbleDashH: DASH_H,
    };
  });
}

// ── Position solving: one static constraint DAG, one forward pass ──────────
//
// Everything that needs a MINIMUM horizontal gap between two commits — same-branch
// succession, a branch's fork-in diagonal, a merge's fork-out diagonal, same-lane
// bubble non-overlap, a cherry-pick's forward order — is expressed as a single
// directed edge `from → to, gap` meaning `x[to] ≥ x[from] + gap`. Every such edge
// goes from an earlier commit to a later one in `rank` order (git history is a DAG,
// and rank strictly increases along any parent→child edge; same-lane adjacency
// follows rank too, since two branches can only share a lane when their rank
// intervals are disjoint — see Phase 3). That makes the whole edge set a genuine
// DAG under rank order, so x can be computed in ONE forward pass (classic
// longest-path-in-a-DAG): visit nodes by ascending rank, set each one's x to the
// max of its own column floor and (source.x + gap) over every incoming edge.
//
// This replaces an earlier design (gael-core-ui / gui, 2026-09-08 through
// 2026-09-10) built from mutually-recursive "chase" functions: pushing one commit
// right re-triggered a same-branch chase, which could re-trigger a merge-gap
// retune, which could push a commit whose own fork-in gap needed re-checking, and
// so on — individually reasonable, but with no global view of the whole
// constraint set. Two concrete failures came out of that: a merge commit could
// render to the right of its own child (nothing walked that specific relationship
// backward), and — the one that matters here — a genuine cycle in the CALL graph
// (not the commit DAG, which has none) between three branches' merge/first-parent
// chases re-triggering each other, bounded only by an ad-hoc reentrancy counter,
// inflating one gap to tens of thousands of pixels before the counter tripped
// (gael-core-ui, 2026-09-10: fix/GU-348 ↔ main ↔ feat/GU-347 — bbd61ba3, b7b5010c,
// 7be05afd, e3062060 each re-pushing the next by ~4000px a lap, ~50 laps deep).
// Modelling every requirement as a fixed-weight edge in a known-acyclic graph up
// front makes that whole class of bug structurally impossible: there is no
// "trigger a fresh pass" step left to cycle through — computing x is a single,
// bounded, non-recursive sweep.
//
// What this design deliberately does NOT do: pull a node further left than its
// minimum once slack exists (the old design's "compaction" and "clean diagonal
// pull-back" passes). A minimum-only solve can leave a visibly longer flat run
// before a fork/merge diagonal than the old code's best-effort tightening did in
// the cases where that tightening didn't itself misfire — a real, known cosmetic
// trade against a structural correctness guarantee. Safe to add back later as a
// bounded, single-direction, non-recursive pass (pull left only, clamped by every
// edge where the node is itself a SOURCE, so it can only ever loosen — never
// violate — a downstream gap) if the extra spread proves visually material.
//
// Ce qui suit lit et MUTE `nodes` (x, bubbleAbove, bubbleDashH) : tout passe par ce contexte
// partagé plutôt que par des fermetures sur computeLayout.
interface SolveContext {
  nodes: LayoutNode[];
  nodeMap: Map<string, LayoutNode>;
  topo: Commit[];
  byRank: Commit[];
  rank: Map<string, number>;
  colX: Map<number, number>;
  bubbleW: Map<string, number>;
  commitTagNames: Map<string, string[]>;
  topoCommitMap: Map<string, Commit>;
  branchDivCommit: Map<string, string>;
  byDivRank: string[];
  cherryPicks: CherryPick[];
}

type IncomingEdges = Map<string, Array<{ from: string; gap: number }>>;

// Invert model.tags (tagName→sha) to sha→tagNames[] — needed both by the tag-width
// term in the lane-adjacency gap below and by fixBubbleLabelTagCollisions further down.
function invertTags(tags: Record<string, string>): Map<string, string[]> {
  const commitTagNames = new Map<string, string[]>();
  for (const [tagName, sha] of Object.entries(tags)) {
    const arr = commitTagNames.get(sha);
    if (arr) arr.push(tagName); else commitTagNames.set(sha, [tagName]);
  }
  return commitTagNames;
}

// Half-width of the widest tag rect on a commit (0 if untagged) — a tagged commit
// is always bubbleAbove:false (see node-building above), so this only ever comes
// into play against an OPPOSITE-side (bubbleAbove:true) neighbour on the same
// lane. That neighbour's dashed stem is a single vertical line straight from its
// dot to its bubble — extending bubbleDashH (fixBubbleLabelTagCollisions below)
// moves the BUBBLE clear of the tag, but the stem itself still runs through
// whatever sits directly below it, so it visually crosses the tag pill whenever
// the neighbour's plain x is within the tag's horizontal footprint. The bare
// MIN_OPPOSITE_SIDE_GAP floor (108) has no idea a tag can be wider than that —
// e.g. a tag like "@gael-core-ui/visualization@1.0.2" is 226.5px wide (half-width
// 113.25), which the bare floor doesn't clear (gael-core-ui, 2026-09-09). The only
// real fix is more horizontal room, not a taller stem.
function tagHalfWidth(commitTagNames: Map<string, string[]>, sha: string): number {
  const tagNames = commitTagNames.get(sha);
  if (!tagNames || tagNames.length === 0) return 0;
  let maxW = 0;
  for (const t of tagNames) maxW = Math.max(maxW, t.length * TAG_CHAR_W + TAG_PAD_X * 2);
  return maxW / 2;
}
const TAG_STEM_MARGIN = 8; // same clearance fixBubbleLabelTagCollisions gives a bubble clearing its own tag

// Same-branch AND cross-branch-sharing-a-lane pairs use the same physical
// adjacency rule, so both are covered by one lane-sequential edge per pair —
// no separate "same branch" case needed (a shared lane's two occupying branches
// never interleave in rank, by the disjoint-interval construction in Phase 3).
function laneAdjacentGap(ctx: SolveContext, prev: LayoutNode, cur: LayoutNode): number {
  let gap = prev.bubbleAbove === cur.bubbleAbove
    ? (ctx.bubbleW.get(prev.sha) ?? 0) / 2 + COMMIT_GAP + (ctx.bubbleW.get(cur.sha) ?? 0) / 2
    : MIN_OPPOSITE_SIDE_GAP;
  if (cur.bubbleAbove) gap = Math.max(gap, tagHalfWidth(ctx.commitTagNames, prev.sha) + TAG_STEM_MARGIN);
  if (prev.bubbleAbove) gap = Math.max(gap, tagHalfWidth(ctx.commitTagNames, cur.sha) + TAG_STEM_MARGIN);
  return gap;
}

// Nearest SAME-SIDE predecessor on a lane can sit further back than the immediate
// physical one whenever an opposite-side node is interposed — two same-side
// bubbles can each clear the (smaller) opposite-side gap against the node
// between them while still overlapping each other directly.
function sameSideGap(ctx: SolveContext, prev: LayoutNode, cur: LayoutNode): number {
  return (ctx.bubbleW.get(prev.sha) ?? 0) / 2 + COMMIT_GAP + (ctx.bubbleW.get(cur.sha) ?? 0) / 2;
}

function addEdge(incoming: IncomingEdges, fromSha: string, toSha: string, gap: number): void {
  const arr = incoming.get(toSha);
  if (arr) arr.push({ from: fromSha, gap }); else incoming.set(toSha, [{ from: fromSha, gap }]);
}

// Same-lane physical adjacency (subsumes same-branch succession, opposite-side
// minimum, same-side bubble width, and tag clearance).
function addLaneAdjacencyEdges(ctx: SolveContext, incoming: IncomingEdges): void {
  const rankOf = (sha: string): number => ctx.rank.get(sha) ?? 0;
  const laneGroups = new Map<number, LayoutNode[]>();
  for (const n of ctx.nodes) {
    const arr = laneGroups.get(n.y);
    if (arr) arr.push(n); else laneGroups.set(n.y, [n]);
  }
  for (const arr of laneGroups.values()) {
    arr.sort((a, b) => rankOf(a.sha) - rankOf(b.sha));
    addEdgesAlongLane(ctx, incoming, arr);
  }
}

function addEdgesAlongLane(ctx: SolveContext, incoming: IncomingEdges, arr: LayoutNode[]): void {
  let lastAbove: LayoutNode | undefined;
  let lastBelow: LayoutNode | undefined;
  for (let i = 0; i < arr.length; i++) {
    const cur = arr[i];
    if (i > 0) addEdge(incoming, arr[i - 1].sha, cur.sha, laneAdjacentGap(ctx, arr[i - 1], cur));
    const nearestSame = cur.bubbleAbove ? lastAbove : lastBelow;
    if (nearestSame) addEdge(incoming, nearestSame.sha, cur.sha, sameSideGap(ctx, nearestSame, cur));
    if (cur.bubbleAbove) lastAbove = cur; else lastBelow = cur;
  }
}

// Branch fork-in: a branch's very first commit vs. the commit it diverged from.
function addForkInEdges(ctx: SolveContext, incoming: IncomingEdges): void {
  for (const bn of ctx.byDivRank) {
    const childSha = ctx.branchDivCommit.get(bn);
    const childNode = childSha ? ctx.nodeMap.get(childSha) : undefined;
    const parentSha = childSha ? ctx.topoCommitMap.get(childSha)?.parents[0] : undefined;
    const parentNode = parentSha ? ctx.nodeMap.get(parentSha) : undefined;
    if (!childNode || !parentNode || parentNode.branchName === childNode.branchName) continue;
    const dy = Math.abs(childNode.y - parentNode.y);
    if (dy === 0) continue;
    addEdge(incoming, parentSha!, childSha!, dy / 4 + MIN_ELBOW_GAP_IN);
  }
}

// Merge fork-out: a merge commit vs. each non-first (source) parent.
function addForkOutEdges(ctx: SolveContext, incoming: IncomingEdges): void {
  for (const c of ctx.topo) {
    if (c.parents.length < 2) continue;
    const childNode = ctx.nodeMap.get(c.hash)!;
    for (let i = 1; i < c.parents.length; i++) {
      const parentNode = ctx.nodeMap.get(c.parents[i]);
      if (!parentNode || parentNode.branchName === childNode.branchName) continue;
      const dy = Math.abs(childNode.y - parentNode.y);
      if (dy === 0) continue;
      addEdge(incoming, c.parents[i], c.hash, dy / 4 + MIN_ELBOW_GAP_OUT);
    }
  }
}

// Cherry-pick: source must render strictly left of target. Only expressible as
// a forward edge — and only safe to add to an acyclic, rank-ordered solve — when
// the source's rank is actually less than the target's; a cherry-pick pointing
// the other way in rank can't be a DAG edge here, so it's left for GitGraph.tsx's
// Z-shape renderer to bend around whatever residual position results.
function addCherryPickEdges(ctx: SolveContext, incoming: IncomingEdges): void {
  const rankOf = (sha: string): number => ctx.rank.get(sha) ?? 0;
  for (const cp of ctx.cherryPicks) {
    if (!ctx.nodeMap.has(cp.sourceSha) || !ctx.nodeMap.has(cp.targetSha)) continue;
    if (rankOf(cp.sourceSha) >= rankOf(cp.targetSha)) continue;
    const srcNode = ctx.nodeMap.get(cp.sourceSha)!;
    const tgtNode = ctx.nodeMap.get(cp.targetSha)!;
    const dy = Math.abs(srcNode.y - tgtNode.y);
    addEdge(incoming, cp.sourceSha, cp.targetSha, Math.max(dy / 4, DOT_R * 3));
  }
}

// Builds every position-constraint edge from CURRENT bubbleAbove — shared by
// solvePositions (uses it with the Phase 4 colX floor) and tightenLeft below (uses
// it WITHOUT that floor, to find each node's true minimum). Rebuilt fresh each
// call rather than cached: bubbleAbove can still change between calls (the
// label/tag collision loop below), and a cheap, linear rebuild is simpler and
// safer than trying to patch a cached edge set incrementally.
function buildPositionEdges(ctx: SolveContext): IncomingEdges {
  const incoming: IncomingEdges = new Map();
  addLaneAdjacencyEdges(ctx, incoming);
  addForkInEdges(ctx, incoming);
  addForkOutEdges(ctx, incoming);
  addCherryPickEdges(ctx, incoming);
  return incoming;
}

// Solves x for every node in one ascending-rank pass: max(Phase 4 column floor,
// every incoming edge's from.x + gap). Called more than once — see
// buildPositionEdges above for why a fresh rebuild each time is deliberate.
function solvePositions(ctx: SolveContext): void {
  const incoming = buildPositionEdges(ctx);
  for (const c of ctx.byRank) {
    const n = ctx.nodeMap.get(c.hash)!;
    let x = ctx.colX.get(ctx.rank.get(c.hash)!)!;
    for (const { from, gap } of incoming.get(c.hash) ?? []) {
      const src = ctx.nodeMap.get(from);
      if (src) x = Math.max(x, src.x + gap);
    }
    n.x = x;
  }
}

// ── Cosmetic left-pull: tighten each node down to its true minimum ─────────
// solvePositions floors every node at colX (Phase 4) — one shared reservation per
// RANK, sized for whichever pair crossing that rank needs the most room, then
// applied to every OTHER pair at that same rank transition too. A pair with a
// much smaller own requirement still gets floored at that shared value, which
// reads as a visibly longer flat run than necessary before its fork/merge
// diagonal — a known, accepted cosmetic cost of the correctness-first design
// above (2026-09-10). This pass removes it: one more forward, rank-ordered pass,
// using ONLY each node's own incoming edges (no colX floor) against its
// predecessors' already-tightened positions.
// Single direction (left only, never right) and safe to do in one linear
// pass with no re-chasing, unlike the recursive push/chase design this file
// replaced: every constraint here has the shape x[to] ≥ x[from] + gap, so
// shrinking x[from] can only ever widen that margin, never violate it — there is
// nothing to check or re-tune on the outgoing side.
// A node with NO incoming edge at all (a lane's very first commit, or a
// truncated root) is left untouched at its Phase 4 column: there is nothing to
// tighten it against, and collapsing every such node to the same left margin
// would destroy the per-rank structure colX exists to give them.
function tightenLeft(ctx: SolveContext): void {
  const incoming = buildPositionEdges(ctx);
  for (const c of ctx.byRank) {
    const edges = incoming.get(c.hash);
    if (!edges || edges.length === 0) continue;
    const n = ctx.nodeMap.get(c.hash)!;
    let x = -Infinity;
    for (const { from, gap } of edges) {
      const src = ctx.nodeMap.get(from);
      if (src) x = Math.max(x, src.x + gap);
    }
    if (x > -Infinity && x < n.x) n.x = x;
  }
}

// ── Branch labels ────────────────────────────────────────────────────────
// Computed as a function of live node positions so it can be rebuilt after a
// later re-solve moves a node — otherwise a label whose underlying commit gets
// repositioned keeps pointing at its old, stale x.
interface LabelContext {
  nodeMap: Map<string, LayoutNode>;
  topo: Commit[];
  branchFirstNode: Map<string, LayoutNode>;
  branchHeads: Record<string, string>;
  commitTagNames: Map<string, string[]>;
  colors: Map<string, string>;
  trunkBranch: string;
  customRules: CustomRules;
}

// Math.abs — symétrique, comme le tracé de l'arête plus bas : une branche
// peut forker au-dessus de sa source (chore/release, via la règle de lane spéciale
// ci-dessus) tout autant qu'en dessous. Sans l'abs(), dxDiag était négatif pour ce cas et
// la garde `firstNode.y > parentNode.y` l'excluait entièrement — le label retombait alors
// sur le fallback "chute verticale sur le premier commit" au lieu de son coude, contrairement
// à toutes les autres branches.
function labelElbow(firstNode: LayoutNode, parentNode: LayoutNode | undefined): { elbowX: number; elbowY: number } {
  if (parentNode && firstNode.y !== parentNode.y) {
    const dxDiag = Math.abs(firstNode.y - parentNode.y) / 4;
    const turnX = parentNode.x + dxDiag;
    if (turnX <= firstNode.x) return { elbowX: turnX, elbowY: firstNode.y };
  }
  // Fallback: straight vertical drop to the first commit (covers main and
  // branches whose fork geometry produces no diagonal elbow point).
  return { elbowX: firstNode.x, elbowY: firstNode.y };
}

// Les tags d'un commit sont dessinés au-dessus de son dot (GitGraph.tsx : TAG_GAP sous la pile,
// TAG_H + TAG_GAP par tag). Un label de branche posé au-dessus d'une lane empiétait sur le tag
// d'un commit de cette lane (le décalage fixe LABEL_ELBOW_ABOVE ne connaît pas les tags) : le
// commit d'ancrage lui-même — typiquement le repli sur le nœud du HEAD quand tous les commits de la
// branche ont été absorbés par une branche prioritaire, voir « Étiquette de branche décalée » dans
// CLAUDE.md — mais aussi un commit voisin sous le label, ou une pile de plusieurs tags. Comme pour
// la hauteur de tige des bulles (extendDashForTags), seuls les tags de la MÊME lane comptent : ceux
// des autres lanes ne sont jamais sur la verticale du label. On remonte le label juste au-dessus de
// la pile la plus haute qu'il chevauche horizontalement (+ TAG_GAP de marge) ; sans chevauchement
// horizontal, rien ne change.
function labelOffsetAboveLane(name: string, labelX: number, laneY: number, ctx: LabelContext): number {
  const labelHalfW = (name.length * LABEL_CHAR_W + LABEL_PAD_X * 2) / 2;
  let offset = LABEL_ELBOW_ABOVE;
  for (const [sha, tagNames] of ctx.commitTagNames) {
    const tagNode = ctx.nodeMap.get(sha);
    if (!tagNode || tagNode.y !== laneY) continue;
    const overlapsTag = tagNames.some((t) => {
      const tagHalfW = (t.length * TAG_CHAR_W + TAG_PAD_X * 2) / 2;
      return labelX + labelHalfW > tagNode.x - tagHalfW && labelX - labelHalfW < tagNode.x + tagHalfW;
    });
    if (!overlapsTag) continue;
    const tagStackHeight = DOT_R + TAG_GAP + (TAG_H + TAG_GAP) * tagNames.length;
    offset = Math.max(offset, tagStackHeight + TAG_GAP + LABEL_H / 2);
  }
  return offset;
}

function computeBranchLabels(ctx: LabelContext): LayoutBranchLabel[] {
  const stackCount = new Map<string, number>();
  const labels: LayoutBranchLabel[] = [];

  for (const bn of Object.keys(ctx.branchHeads)) {
    // If the branch has no unique commits (all claimed by a higher-priority branch,
    // e.g. a single-commit release fast-forwarded into main), fall back to the node
    // for the branch's HEAD SHA so its label is still rendered.
    const firstNode = ctx.branchFirstNode.get(bn) ?? ctx.nodeMap.get(ctx.branchHeads[bn]);
    if (!firstNode) continue;

    const firstCommit = ctx.topo.find((c) => c.hash === firstNode.sha);
    const parentHash = firstCommit?.parents[0];
    const parentNode = parentHash ? ctx.nodeMap.get(parentHash) : undefined;
    const { elbowX, elbowY } = labelElbow(firstNode, parentNode);

    const idx = stackCount.get(firstNode.sha) ?? 0;
    stackCount.set(firstNode.sha, idx + 1);
    labels.push({
      name: bn,
      x: elbowX,
      y: firstNode.y - labelOffsetAboveLane(bn, elbowX, firstNode.y, ctx) - idx * (LABEL_H + 4),
      // colors.get(bn) misses when bn's commits were all absorbed into a higher-priority
      // branch (Refs stale gotcha) — it never got its own lane in `allBranches`. Falling back
      // to MAIN_BLUE there was wrong: recompute via branchColor() so the label still gets the
      // color its own name/rules dictate (e.g. FEATURE_GREEN for feat/*), not an arbitrary blue.
      color: ctx.colors.get(bn) ?? branchColor(bn, 0, ctx.trunkBranch, ctx.customRules),
      elbowX,
      elbowY,
    });
  }
  return labels;
}

// ── Fix bubble-above collision with branch labels and tag labels ─────────────
// If a node's above-bubble rect overlaps any branch-label pill or tag rect,
// flip it to bubble-below. Only ever flips true→false, never back — so calling
// it repeatedly only ever fixes new collisions, never reopens one already
// resolved, which is what makes the bounded solve/label/flip loop below safe:
// it can only converge (fewer bubbleAbove:true candidates each round), never
// oscillate.
interface BubbleRect { top: number; bottom: number; left: number; right: number }

function bubbleRect(node: LayoutNode): BubbleRect {
  const { boxH, boxW } = computeBubble(node.subject);
  return {
    top: node.y - DOT_R - DASH_H - boxH,
    bottom: node.y - DOT_R,
    left: node.x - boxW / 2,
    right: node.x + boxW / 2,
  };
}

function overlapsAnyLabel(rect: BubbleRect, labels: LayoutBranchLabel[]): boolean {
  for (const label of labels) {
    const lW     = label.name.length * LABEL_CHAR_W + LABEL_PAD_X * 2;
    const lTop   = label.y - LABEL_H / 2;
    const lBottom = label.y + LABEL_H / 2;
    const lLeft  = label.x - lW / 2;
    const lRight = label.x + lW / 2;
    if (rect.bottom > lTop && rect.top < lBottom && rect.right > lLeft && rect.left < lRight) return true;
  }
  return false;
}

// Check horizontal overlap with the widest tag on this commit
function overlapsTagHorizontally(rect: BubbleRect, tagNode: LayoutNode, tagNames: string[]): boolean {
  for (const name of tagNames) {
    const tw    = name.length * TAG_CHAR_W + TAG_PAD_X * 2;
    const tLeft  = tagNode.x - tw / 2;
    const tRight = tagNode.x + tw / 2;
    if (rect.right > tLeft && rect.left < tRight) return true;
  }
  return false;
}

// Tag labels: extend bubbleDashH so the bubble clears all horizontally-overlapping tags.
// Only consider tags on the same lane (same y) — tags on other lanes are never in the
// vertical path of an above-bubble and would produce a wildly inflated dash height.
function extendDashForTags(
  node: LayoutNode,
  rect: BubbleRect,
  nodeMap: Map<string, LayoutNode>,
  commitTagNames: Map<string, string[]>,
): void {
  for (const [sha, tagNames] of commitTagNames) {
    const tagNode = nodeMap.get(sha);
    if (!tagNode || tagNode.y !== node.y) continue;
    if (!overlapsTagHorizontally(rect, tagNode, tagNames)) continue;
    // Push bubble above the topmost tag on this commit (+ 4 px margin)
    const topTagY = tagNode.y - DOT_R - TAG_GAP - (TAG_H + TAG_GAP) * tagNames.length;
    node.bubbleDashH = Math.max(node.bubbleDashH, node.y - DOT_R - topTagY + 8);
  }
}

function fixBubbleLabelTagCollisions(ctx: SolveContext, labels: LayoutBranchLabel[]): LayoutNode[] {
  const flipped: LayoutNode[] = [];
  for (const node of ctx.nodes) {
    if (!node.bubbleAbove) continue;
    const rect = bubbleRect(node);
    if (overlapsAnyLabel(rect, labels)) {
      node.bubbleAbove = false;
      flipped.push(node);
      continue;
    }
    extendDashForTags(node, rect, ctx.nodeMap, ctx.commitTagNames);
  }
  return flipped;
}

// Solve → label → flip, repeated until a round flips nothing. bubbleAbove feeds
// the gap formulas above, so a flip can change a downstream minimum gap — a fresh,
// cheap, linear re-solve keeps every position consistent with the CURRENT
// bubbleAbove rather than accepting stale gaps from before the flip. Bounded at 4
// rounds as a hard backstop: flips are monotonic (true→false only) so this always
// terminates well before that in practice, but nothing here should ever be
// unbounded again after 2026-09-10's runaway.
//
// Then, once positions and bubbleAbove have both settled: tightening moves
// nodes closer together, so doing it any earlier would just get overwritten by
// the next solvePositions() in that loop. Labels are rebuilt and the collision
// check re-run once more (not re-looped) for the same reason the second
// fixBubbleLabelTagCollisions pass exists elsewhere in this file: only-ever-
// forward flips make a single extra pass safe without needing to re-converge —
// tightening can only pull a bubble closer to a label it was already clear of,
// never further away.
//
// But a flip here changes more than label clearance: the lane-neighbor gap
// formula depends on both nodes' bubbleAbove (same side needs roughly double the
// gap of opposite sides — see laneAdjacentGap), and tightenLeft() already baked
// in the PRE-flip gap. Left uncorrected, a flip right after tightening can leave
// a node's x satisfying the old (smaller) gap but not the new one, overlapping
// its same-lane neighbor's bubble (found 2026-09-10, repo "gui",
// 2b10a6c7.../c491d75f...). One extra solve+tighten round fixes it; no need to
// re-loop the label-collision check itself, per the monotonic-flip reasoning above.
function solveNodePositions(ctx: SolveContext, labelCtx: LabelContext): LayoutBranchLabel[] {
  solvePositions(ctx);
  let branchLabels = computeBranchLabels(labelCtx);
  for (let i = 0; i < 4; i++) {
    const flipped = fixBubbleLabelTagCollisions(ctx, branchLabels);
    if (flipped.length === 0) break;
    solvePositions(ctx);
    branchLabels = computeBranchLabels(labelCtx);
  }

  tightenLeft(ctx);
  branchLabels = computeBranchLabels(labelCtx);
  if (fixBubbleLabelTagCollisions(ctx, branchLabels).length > 0) {
    solvePositions(ctx);
    tightenLeft(ctx);
    // Positions moved again after branchLabels was built above — rebuild it, same
    // reason computeBranchLabels is re-called after every other solve in this file
    // (see the "branch label offset" gotcha in CLAUDE.md): a label built from stale
    // x/elbowX would render pointing at where its dot used to be.
    branchLabels = computeBranchLabels(labelCtx);
  }
  return branchLabels;
}

// ── Truncated history markers ─────────────────────────────────────────────
// A commit whose first parent SHA is absent from the dataset sits at the
// edge of the per-branch fetch window.  Render a ghost node to the left so
// the viewer knows the history continues beyond what is displayed.
const GHOST_OFFSET = 50;
function buildTruncatedRoots(topo: Commit[], nodeMap: Map<string, LayoutNode>): TruncatedRoot[] {
  const knownHashes = new Set(topo.map((c) => c.hash));
  const truncatedRoots: TruncatedRoot[] = [];
  for (const c of topo) {
    if (c.parents.length === 0 || knownHashes.has(c.parents[0])) continue;
    const node = nodeMap.get(c.hash)!;
    truncatedRoots.push({
      sha: c.hash,
      childX: node.x,
      childY: node.y,
      x: Math.max(DOT_R * 3, node.x - GHOST_OFFSET),
      y: node.y,
      color: node.color,
    });
  }
  return truncatedRoots;
}

// ── Build edges ─────────────────────────────────────────────────────────
// Non-first-parent (merge source → merge commit).  The solve above already
// gave the pair at least dy/4 + MIN_ELBOW_GAP_OUT of room.  If the gap is
// wider than that (its same-lane neighbours needed more room than the bare
// merge minimum): prefer continuing straight along the source's own lane
// for a while, then bending into a single ±4 diagonal up to the merge
// commit — reads as "the branch keeps going, then merges" instead of a
// bridge floating between two lanes. Only safe when no other dot sits on
// that lane between the source and the bend point (it would then look like
// the line runs along that branch); otherwise fall back to a horizontal
// middle snapped halfway between two lanes, clear of every lane's row.
// A too-STEEP residual (below the solved minimum — topology left no room,
// e.g. a same-lane neighbour further constrained it) can't get a clean
// horizontal middle either, so it stays a single straight segment.
function mergeEdgePath(child: LayoutNode, parent: LayoutNode, nodes: LayoutNode[], laneSpacing: number): string {
  const eDx  = child.x - parent.x;
  const eDy  = Math.abs(child.y - parent.y);
  const eAdx = Math.abs(eDx);
  const eDir = eDx >= 0 ? 1 : -1;
  if (eAdx < eDy / 4 + MIN_ELBOW_GAP_OUT) return `M ${parent.x} ${parent.y} L ${child.x} ${child.y}`;

  const dxDiag = eDy / 4;
  const turnX  = child.x - eDir * dxDiag;
  const laneClear = !nodes.some((n) => n !== parent && n.y === parent.y
    && (eDir > 0 ? n.x > parent.x && n.x < turnX : n.x < parent.x && n.x > turnX));
  if (laneClear) return `M ${parent.x} ${parent.y} L ${turnX} ${parent.y} L ${child.x} ${child.y}`;

  const midYideal = (parent.y + child.y) / 2;
  const midY      = Math.floor(midYideal / laneSpacing) * laneSpacing + laneSpacing / 2;
  // The half-lane snap above only leaves both diagonals symmetric (each
  // spanning eDy/2) when midYideal already sits on a half-lane itself —
  // i.e. parent/child lanes have different parity. Same parity shifts midY
  // by laneSpacing/2 off the true midpoint, making the two segments'
  // vertical spans unequal. Deriving each segment's dx from its own actual
  // dy (rather than a single shared eDy/8) keeps both at slope 4 regardless.
  const seg1 = Math.abs(midY - parent.y) / 4;
  const seg2 = Math.abs(child.y - midY) / 4;
  return `M ${parent.x} ${parent.y} L ${parent.x + eDir * seg1} ${midY} L ${child.x - eDir * seg2} ${midY} L ${child.x} ${child.y}`;
}

// Branch creation (first parent, any direction): leaves the source rail
// on a ±4 diagonal immediately — never lingers on the source's own lane
// first — then runs flat on the new branch's own rail to reach the
// child. Sign of the diagonal follows whether the new branch sits below
// (+4, the common case) or above (-4, e.g. chore/release) its source;
// the shape itself is the same either way. The solve above guarantees
// enough room for turnX to land at or before child.x, so the steep/no-room
// fallback below is only a safety net, not the expected path.
function branchCreationPath(child: LayoutNode, parent: LayoutNode): string {
  const dxDiag = Math.abs(child.y - parent.y) / 4;
  const turnX = parent.x + dxDiag;
  return turnX <= child.x
    ? `M ${parent.x} ${parent.y} L ${turnX} ${child.y} L ${child.x} ${child.y}`
    : `M ${parent.x} ${parent.y} L ${child.x} ${child.y}`;
}

function edgePath(child: LayoutNode, parent: LayoutNode, parentIndex: number, nodes: LayoutNode[], laneSpacing: number): string {
  if (child.y === parent.y) return `M ${parent.x} ${parent.y} L ${child.x} ${child.y}`;
  if (parentIndex > 0) return mergeEdgePath(child, parent, nodes, laneSpacing);
  return branchCreationPath(child, parent);
}

// First-parent edges use child color; merge (non-first) edges use parent color.
// Built last (after every x-adjustment above) so paths always match the final
// dot positions.
function buildLayoutEdges(
  topo: Commit[],
  nodeMap: Map<string, LayoutNode>,
  nodes: LayoutNode[],
  laneSpacing: number,
): LayoutEdge[] {
  const edges: LayoutEdge[] = [];
  for (const c of topo) {
    const child = nodeMap.get(c.hash)!;
    for (const [i, parentHash] of c.parents.entries()) {
      const parent = nodeMap.get(parentHash);
      if (!parent) continue;

      const color      = i === 0 ? child.color  : parent.color;
      const branchName = i === 0 ? child.branchName : parent.branchName;
      const path = edgePath(child, parent, i, nodes, laneSpacing);
      edges.push({ id: `${parentHash}-${c.hash}`, path, color, branchName, isMerge: i > 0 });
    }
  }
  return edges;
}

// ── Open tips ─────────────────────────────────────────────────────────────
// A branch is "open" (ongoing) if its rightmost node in the dataset is NOT
// a non-first-parent merge source (which would already carry a merge arrow).
// Using the rightmost node (max X) rather than allBranchHeads avoids failures
// when the true HEAD SHA is beyond the fetch window and absent from nodeMap.
function computeOpenTips(topo: Commit[], nodes: LayoutNode[]): LayoutNode[] {
  const mergeSourceShas = new Set<string>();
  for (const c of topo) {
    for (let i = 1; i < c.parents.length; i++) mergeSourceShas.add(c.parents[i]);
  }
  const branchRightmost = new Map<string, LayoutNode>();
  for (const node of nodes) {
    const cur = branchRightmost.get(node.branchName);
    if (!cur || node.x > cur.x) branchRightmost.set(node.branchName, node);
  }
  const seenTipSha = new Set<string>();
  const openTips: LayoutNode[] = [];
  for (const node of branchRightmost.values()) {
    if (!mergeSourceShas.has(node.sha) && !seenTipSha.has(node.sha)) {
      openTips.push(node);
      seenTipSha.add(node.sha);
    }
  }
  return openTips;
}

// Colors indexed by divergence order (stable regardless of lane reuse)
function assignBranchColors(
  trunkBranch: string,
  byDivRank: string[],
  customRules: CustomRules,
): Map<string, string> {
  const colors = new Map<string, string>();
  [trunkBranch, ...byDivRank].forEach((bn, i) => colors.set(bn, branchColor(bn, i, trunkBranch, customRules)));
  return colors;
}

// Semantic trunk: prefer 'main'/'master' when present so that projects where
// the GitLab default branch is a release/* branch still render with main on
// lane 0 rather than the release branch.
function pickTrunkBranch(model: GitgraphModel, defaultBranch: string): string {
  return ['main', 'master'].find((n) => model.branchHeads[n] !== undefined) ?? defaultBranch;
}

export function computeLayout(
  model: GitgraphModel,
  defaultBranch: string,
  cherryPicks: CherryPick[] = [],
  branchColorRules: BranchColorRule[] = [],
): Layout {
  const customRules = compileCustomRules(branchColorRules);
  if (model.commits.length === 0) {
    return { nodes: [], edges: [], branchLabels: [], truncatedRoots: [], openTips: [], trunkBranch: defaultBranch, laneSpacing: 120, svgWidth: 200, svgHeight: 200 };
  }

  const topo = topoSort(model.commits); // parents before children
  const trunkBranch = pickTrunkBranch(model, defaultBranch);

  const rank = computeRanks(topo);
  const branchOfCommit = assignBranchOfCommit(topo, trunkBranch);
  const intervals = computeBranchIntervals(topo, rank, branchOfCommit, trunkBranch, cherryPicks);
  const hierarchy = computeBranchHierarchy(topo, branchOfCommit, intervals, trunkBranch, model.protectedBranches);
  const { branchLane, byDivRank } = assignLanes(trunkBranch, intervals, hierarchy);
  const colors = assignBranchColors(trunkBranch, byDivRank, customRules);
  const { colX, bubbleW, laneSpacing } = computeColumns(topo, rank, branchOfCommit, branchLane, trunkBranch);

  const nodes = buildLayoutNodes(topo, model, rank, branchOfCommit, branchLane, colors, colX, laneSpacing);
  const nodeMap = new Map(nodes.map((n) => [n.sha, n]));
  const branchFirstNode = new Map<string, LayoutNode>();
  for (const node of nodes) {
    if (!branchFirstNode.has(node.branchName)) branchFirstNode.set(node.branchName, node);
  }

  const commitTagNames = invertTags(model.tags);
  const solveCtx: SolveContext = {
    nodes, nodeMap, topo, rank, colX, bubbleW, cherryPicks, byDivRank, commitTagNames,
    byRank: [...topo].sort((a, b) => rank.get(a.hash)! - rank.get(b.hash)!),
    topoCommitMap: new Map(topo.map((c) => [c.hash, c])),
    branchDivCommit: intervals.branchDivCommit,
  };
  const labelCtx: LabelContext = {
    nodeMap, topo, branchFirstNode, colors, trunkBranch, customRules, commitTagNames,
    branchHeads: model.branchHeads,
  };
  const branchLabels = solveNodePositions(solveCtx, labelCtx);

  const maxX = Math.max(...nodes.map((n) => n.x), 0);
  const maxY = Math.max(...nodes.map((n) => n.y), 0);

  return {
    nodes,
    edges: buildLayoutEdges(topo, nodeMap, nodes, laneSpacing),
    branchLabels,
    truncatedRoots: buildTruncatedRoots(topo, nodeMap),
    openTips: computeOpenTips(topo, nodes),
    trunkBranch,
    laneSpacing,
    svgWidth: maxX + 200,
    svgHeight: maxY + laneSpacing,
  };
}
